import React, { useState, useEffect, useMemo } from 'react';
import {
  Box,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
} from '@mui/material';
import {
  FilterList as FilterIcon,
} from '@mui/icons-material';

/**
 * ScopeSelector — cascading dropdown filters for site, department, team.
 * Department dropdown is filtered by selected site.
 * Team dropdown is filtered by selected department.
 * "All" option at each level shows org-wide / site-wide / dept-wide data.
 * Dashboard data updates on scope change.
 */
function ScopeSelector({ value, onChange, organization, lockScope = null }) {
  const [selectedSite, setSelectedSite] = useState('all');
  const [selectedDepartment, setSelectedDepartment] = useState('all');
  const [selectedTeam, setSelectedTeam] = useState('all');

  // Scoped members (site managers, DHs, FLS/ITMs) are fenced to their own
  // branch of the org — they can drill DOWN within it but never sideways/up.
  const lockSiteId = lockScope?.siteId || null;
  const lockDeptId = lockScope?.deptId || null;
  const lockTeamId = lockScope?.nwtId || null;

  const sites = useMemo(() => {
    const all = organization?.sites || [];
    return lockSiteId ? all.filter((s) => s.id === lockSiteId) : all;
  }, [organization?.sites, lockSiteId]);
  const departments = useMemo(() => {
    const all = organization?.departments || [];
    return lockDeptId ? all.filter((d) => d.id === lockDeptId) : all;
  }, [organization?.departments, lockDeptId]);
  const teams = useMemo(() => {
    const all = organization?.teams || [];
    return lockTeamId ? all.filter((t) => t.id === lockTeamId) : all;
  }, [organization?.teams, lockTeamId]);

  // Filter departments by selected site
  const filteredDepartments = useMemo(() => {
    if (selectedSite === 'all') return departments;
    return departments.filter((dept) => dept.siteId === selectedSite);
  }, [departments, selectedSite]);

  // Filter teams by selected department
  const filteredTeams = useMemo(() => {
    if (selectedDepartment === 'all') {
      // If a site is selected but no department, show teams for that site
      if (selectedSite !== 'all') {
        const siteDeptIds = filteredDepartments.map((d) => d.id);
        return teams.filter((team) => siteDeptIds.includes(team.departmentId));
      }
      return teams;
    }
    return teams.filter((team) => team.departmentId === selectedDepartment);
  }, [teams, selectedDepartment, selectedSite, filteredDepartments]);

  // Sync internal state from external value prop
  useEffect(() => {
    if (!value || value.type === 'organization') {
      setSelectedSite('all');
      setSelectedDepartment('all');
      setSelectedTeam('all');
    } else if (value.type === 'site') {
      setSelectedSite(value.id || 'all');
      setSelectedDepartment('all');
      setSelectedTeam('all');
    } else if (value.type === 'department') {
      // Find the site this department belongs to
      const dept = departments.find((d) => d.id === value.id);
      if (dept?.siteId) setSelectedSite(dept.siteId);
      setSelectedDepartment(value.id || 'all');
      setSelectedTeam('all');
    } else if (value.type === 'team') {
      const team = teams.find((t) => t.id === value.id);
      if (team?.departmentId) {
        setSelectedDepartment(team.departmentId);
        const dept = departments.find((d) => d.id === team.departmentId);
        if (dept?.siteId) setSelectedSite(dept.siteId);
      }
      setSelectedTeam(value.id || 'all');
    }
  }, [value?.type, value?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleSiteChange = (e) => {
    const siteId = e.target.value;
    setSelectedSite(siteId);
    setSelectedDepartment('all');
    setSelectedTeam('all');

    if (siteId === 'all') {
      onChange({ type: 'organization', id: null });
    } else {
      onChange({ type: 'site', id: siteId });
    }
  };

  const handleDepartmentChange = (e) => {
    const deptId = e.target.value;
    setSelectedDepartment(deptId);
    setSelectedTeam('all');

    if (deptId === 'all') {
      if (selectedSite === 'all') {
        onChange({ type: 'organization', id: null });
      } else {
        onChange({ type: 'site', id: selectedSite });
      }
    } else {
      onChange({ type: 'department', id: deptId });
    }
  };

  const handleTeamChange = (e) => {
    const teamId = e.target.value;
    setSelectedTeam(teamId);

    if (teamId === 'all') {
      if (selectedDepartment !== 'all') {
        onChange({ type: 'department', id: selectedDepartment });
      } else if (selectedSite !== 'all') {
        onChange({ type: 'site', id: selectedSite });
      } else {
        onChange({ type: 'organization', id: null });
      }
    } else {
      onChange({ type: 'team', id: teamId });
    }
  };

  // Determine scope label for display
  const getScopeLabel = () => {
    if (selectedTeam !== 'all') {
      const team = teams.find((t) => t.id === selectedTeam);
      return team?.name || 'Team';
    }
    if (selectedDepartment !== 'all') {
      const dept = departments.find((d) => d.id === selectedDepartment);
      return dept?.name || 'Department';
    }
    if (selectedSite !== 'all') {
      const site = sites.find((s) => s.id === selectedSite);
      return site?.name || 'Site';
    }
    return organization?.name || 'All';
  };

  const isFiltered = selectedSite !== 'all' || selectedDepartment !== 'all' || selectedTeam !== 'all';

  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
      {isFiltered && (
        <Chip
          icon={<FilterIcon />}
          label={getScopeLabel()}
          size="small"
          color="primary"
          variant="outlined"
          onDelete={
            lockSiteId || lockDeptId || lockTeamId
              ? undefined // locked scopes can't be cleared back to org-wide
              : () => {
                  setSelectedSite('all');
                  setSelectedDepartment('all');
                  setSelectedTeam('all');
                  onChange({ type: 'organization', id: null });
                }
          }
          sx={{ mr: 0.5 }}
        />
      )}

      {/* Site Selector */}
      <FormControl size="small" sx={{ minWidth: 150 }}>
        <InputLabel>Site</InputLabel>
        <Select value={selectedSite} onChange={handleSiteChange} label="Site" disabled={Boolean(lockSiteId)}>
          {!lockSiteId && (
            <MenuItem value="all">
              All Sites
            </MenuItem>
          )}
          {sites.map((site) => (
            <MenuItem key={site.id} value={site.id}>
              {site.name}
            </MenuItem>
          ))}
        </Select>
      </FormControl>

      {/* Department Selector — filtered by selected site */}
      {(filteredDepartments.length > 0 || selectedSite !== 'all') && (
        <FormControl size="small" sx={{ minWidth: 160 }}>
          <InputLabel>Department</InputLabel>
          <Select
            value={selectedDepartment}
            onChange={handleDepartmentChange}
            label="Department"
            disabled={filteredDepartments.length === 0 || Boolean(lockDeptId)}
          >
            {!lockDeptId && (
              <MenuItem value="all">
                All Departments
              </MenuItem>
            )}
            {filteredDepartments.map((dept) => (
              <MenuItem key={dept.id} value={dept.id}>
                {dept.name}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      )}

      {/* Team Selector — filtered by selected department */}
      {(filteredTeams.length > 0 || selectedDepartment !== 'all') && (
        <FormControl size="small" sx={{ minWidth: 140 }}>
          <InputLabel>NWT</InputLabel>
          <Select
            value={selectedTeam}
            onChange={handleTeamChange}
            label="Team"
            disabled={filteredTeams.length === 0 || Boolean(lockTeamId)}
          >
            {!lockTeamId && (
              <MenuItem value="all">
                All NWTs
              </MenuItem>
            )}
            {filteredTeams.map((team) => (
              <MenuItem key={team.id} value={team.id}>
                {team.name}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      )}
    </Box>
  );
}

export default ScopeSelector;
